import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MonitorSmartphone,
  Wifi,
  WifiOff,
  AlertTriangle,
  CreditCard,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API } from '@/config/api';
import { BO_BASE } from '@/utils/getBasePath';

const bo = (route = '') => (route ? `${BO_BASE}/${route}` : BO_BASE || '/');

/**
 * Compact dashboard tile that surfaces kiosk fleet health.
 *
 * Polls at most every 5 minutes (per product spec — no aggressive polling).
 * Read-only — never mutates device state.
 */
export default function KioskFleetTile() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchFleet = useCallback(async () => {
    try {
      const token =
        localStorage.getItem('backoffice_token') ||
        localStorage.getItem('merchant_token') ||
        localStorage.getItem('access_token');
      if (!token) {
        setError('not_authenticated');
        setLoading(false);
        return;
      }
      const res = await axios.get(`${API}/owner/kiosks/fleet-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(res.data.summary || null);
      setLastUpdated(res.data.generated_at || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'unknown');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFleet();
    // 5-minute refresh interval — gentle on the backend.
    const t = setInterval(fetchFleet, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchFleet]);

  const total = summary?.total ?? 0;
  const online = summary?.online ?? 0;
  const offline = summary?.offline ?? 0;
  const stale = summary?.stale ?? 0;
  const misconfigured = summary?.terminal_misconfigured ?? 0;

  const hasAlert = offline > 0 || misconfigured > 0 || stale > 0;

  return (
    <Card
      data-testid="kiosk-fleet-tile"
      className="bg-white border-0 shadow-sm"
    >
      <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm sm:text-base font-semibold text-gray-800 flex items-center gap-2">
            <MonitorSmartphone className="h-4 w-4 text-slate-500" />
            Kiosk Fleet
          </CardTitle>
          <button
            type="button"
            onClick={fetchFleet}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Refresh kiosk fleet"
            data-testid="kiosk-fleet-tile-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </CardHeader>
      <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
        {error ? (
          <div className="py-4 text-center">
            <AlertTriangle className="h-5 w-5 text-amber-500 mx-auto mb-1" />
            <p className="text-xs text-slate-500">
              {error === 'not_authenticated'
                ? 'Sign in to view kiosks'
                : 'Unable to load fleet health'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <FleetStat
                testId="kiosk-fleet-stat-total"
                label="Total kiosks"
                value={total}
                icon={<MonitorSmartphone className="h-4 w-4 text-slate-500" />}
              />
              <FleetStat
                testId="kiosk-fleet-stat-online"
                label="Online"
                value={online}
                icon={<Wifi className="h-4 w-4 text-emerald-500" />}
                valueClass="text-emerald-600"
              />
              <FleetStat
                testId="kiosk-fleet-stat-offline"
                label="Offline"
                value={offline}
                icon={<WifiOff className="h-4 w-4 text-rose-500" />}
                valueClass={offline > 0 ? 'text-rose-600' : 'text-slate-600'}
              />
              <FleetStat
                testId="kiosk-fleet-stat-misconfigured"
                label="Terminal not ready"
                value={misconfigured}
                icon={<CreditCard className="h-4 w-4 text-amber-500" />}
                valueClass={
                  misconfigured > 0 ? 'text-amber-600' : 'text-slate-600'
                }
              />
            </div>

            {stale > 0 && (
              <p
                className="mt-3 text-xs text-amber-700 flex items-center gap-1"
                data-testid="kiosk-fleet-stale-note"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                {stale} kiosk{stale === 1 ? '' : 's'} stale (10–30 min)
              </p>
            )}

            <Button
              variant="ghost"
              className="w-full mt-3 justify-between text-sm"
              onClick={() => navigate(bo('kiosks'))}
              data-testid="kiosk-fleet-open-page-button"
            >
              <span>{hasAlert ? 'Investigate fleet' : 'View kiosk fleet'}</span>
              <ChevronRight className="h-4 w-4" />
            </Button>

            {lastUpdated && (
              <p className="mt-2 text-[10px] text-slate-400 text-center">
                Updated {new Date(lastUpdated).toLocaleTimeString()}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function FleetStat({ label, value, icon, valueClass = 'text-slate-800', testId }) {
  return (
    <div
      data-testid={testId}
      className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
    >
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <p className={`text-xl font-bold ${valueClass}`}>{value}</p>
    </div>
  );
}
